import { Box, TextField, Typography, MenuItem, Chip, Tooltip } from "@mui/material";
import { Controller } from "react-hook-form";

const FormField = ({
  name, label, placeholder, required, multiline, rows, asterisk, pattern,
  register, formErrors, defaultValue, className, select, options, control,
  disabled, InputProps, type, error, helperText
}) => {
  const commonProps = {
    label,
    placeholder,
    fullWidth: true,
    error: error !== undefined ? error : !!formErrors[name],
    helperText: helperText !== undefined ? helperText : formErrors[name]?.message,
    className,
    disabled,
    InputLabelProps: { shrink: true },
    InputProps
  };

  // Check if field should be a select or multiselect
  const isSelect = select || type === "select" || type === "multiselect";
  const isMultiple = type === "multiselect";

  if (isSelect) {
    return (
      <Box sx={{ position: "relative" }}>
        {asterisk && (
          <Typography
            variant="caption"
            color="#FF0000"
            className="mandatoryAsterisk"
          >
            *
          </Typography>
        )}
        <Controller
          name={name}
          control={control}
          defaultValue={isMultiple ? (defaultValue || []) : (defaultValue || "")}
          rules={{
            required: required ? { value: true, message: required } : false
          }}
          render={({ field }) => (
            <TextField 
              {...field} 
              select 
              {...commonProps}
              SelectProps={{ 
                multiple: isMultiple,
                ...(isMultiple && {
                  renderValue: (selected) => {
                    if (selected.length === 0) return '';
                    
                    // Get display labels for selected values
                    const selectedLabels = selected.map(value => {
                      const option = options?.find(opt => 
                        (typeof opt === 'object' ? opt.value : opt) === value
                      );
                      return typeof option === 'object' ? option.label : option;
                    });
                    
                    // Create display text
                    const displayText = selectedLabels.join(', ');
                    const shouldTruncate = displayText.length > 40;
                    const truncatedText = shouldTruncate 
                      ? `${displayText.substring(0, 37)}...` 
                      : displayText;
                    
                    // Show tooltip if multiple items selected or text is truncated
                    const showTooltip = selected.length > 1 || shouldTruncate;
                    
                    return (
                      <Tooltip 
                        title={showTooltip ? displayText : ''} 
                        placement="top-start"
                        arrow
                        enterDelay={300}
                        leaveDelay={200}
                      >
                        <Box 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            cursor: showTooltip ? 'help' : 'default'
                          }}
                        >
                          {truncatedText}
                        </Box>
                      </Tooltip>
                    );
                  }
                })
              }}
            >
              {/* Only show placeholder for single select */}
              {!isMultiple && <MenuItem value="">{placeholder}</MenuItem>}
              
              {options &&
                options.map((option) =>
                  typeof option === "object" &&
                  option !== null &&
                  "value" in option &&
                  "label" in option ? (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ) : (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )
                )}
            </TextField>
          )}
        />
      </Box>
    );
  } else {
    return (
      <Box sx={{ position: "relative" }}>
        {asterisk && (
          <Typography
            variant="caption"
            color="#FF0000"
            className="mandatoryAsterisk"
          >
            *
          </Typography>
        )}
        <TextField
          {...register(name, {
            required: required ? required : false,
            ...(pattern ? { pattern } : {})
          })}
          {...commonProps}
          type={type}
          multiline={multiline}
          rows={rows}
          defaultValue={defaultValue}
        />
      </Box>
    );
  }
};

export default FormField;